import type { FinalTiebreakRule } from "./types.js";
import type { RoundResult } from "./formats/types.js";

export interface StandingEntry {
  playerId: string;
  totalPoints: number;
  /** Shared between tied players; only split apart when the configured rule actually breaks the tie. */
  placement: number;
}

function roundWinners(rr: RoundResult): Set<string> {
  const values = Object.values(rr.points);
  const max = values.length ? Math.max(...values) : 0;
  const winners = new Set<string>();
  if (max <= 0) return winners;
  for (const [id, pts] of Object.entries(rr.points)) if (pts === max) winners.add(id);
  return winners;
}

/**
 * Overall standings across rounds. 'split' (the default) never breaks a tie —
 * tied players share a placement. The other rules only kick in to separate players
 * whose total points are exactly equal (spec §6).
 */
export function computeFinalStandings(
  playerIds: string[],
  roundResults: RoundResult[],
  rule: FinalTiebreakRule,
  manualOrder?: string[],
): StandingEntry[] {
  const totalPoints = new Map<string, number>();
  for (const id of playerIds) totalPoints.set(id, 0);
  for (const rr of roundResults) {
    for (const [id, pts] of Object.entries(rr.points)) {
      totalPoints.set(id, (totalPoints.get(id) ?? 0) + pts);
    }
  }

  const breaksTies = rule !== "split" && !(rule === "manual" && !manualOrder);

  const secondaryKey = (id: string): number => {
    switch (rule) {
      case "mostRoundWins":
        return roundResults.filter((rr) => roundWinners(rr).has(id)).length;
      case "bestSingleRound":
        return roundResults.reduce((max, rr) => Math.max(max, rr.points[id] ?? 0), 0);
      case "lowestNetTotal": {
        let sum = 0;
        for (const rr of roundResults) {
          if (!rr.netTotal) continue; // this round's format doesn't track net totals
          const net = rr.netTotal[id];
          // Missing here (unlike a missing netTotal map above) means the player didn't
          // complete this round — treating that as a net 0 would score a DNF as the
          // best possible round instead of excluding it, so it loses this tiebreak.
          if (net == null) return -Infinity;
          sum += net;
        }
        return -sum;
      }
      case "manual":
        return manualOrder ? -manualOrder.indexOf(id) : 0;
      default:
        return 0;
    }
  };

  const sorted = [...playerIds].sort((a, b) => {
    const primary = totalPoints.get(b)! - totalPoints.get(a)!;
    if (primary !== 0) return primary;
    return breaksTies ? secondaryKey(b) - secondaryKey(a) : 0;
  });

  const entries: StandingEntry[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (
      j + 1 < sorted.length &&
      totalPoints.get(sorted[j + 1]!) === totalPoints.get(sorted[i]!) &&
      (!breaksTies || secondaryKey(sorted[j + 1]!) === secondaryKey(sorted[i]!))
    ) {
      j++;
    }
    for (let k = i; k <= j; k++) {
      entries.push({
        playerId: sorted[k]!,
        totalPoints: totalPoints.get(sorted[k]!)!,
        placement: i + 1,
      });
    }
    i = j + 1;
  }

  return entries;
}
